import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import Button from '../../../components/atoms/Button';
import BookmarkPopover from '../../../components/reportsWidgets/BookmarkPopover';
import { Reports } from '../../../services';
import TopbarService from '../../../services/topbar';
import toaster from '../../../utils/toaster';
import { useAsync } from '../../../utils/customHooks';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import { getUserData } from '../../../selectors/user';
import { dispatchBookmarkEvent, decodeBookmarkName } from '../../../topbar/bookmarkUtils';
import { BOOKMARK_EVENT } from '../../../topbar/consts';
import { ANOMALY_ANALYSIS_REPORT_TYPE, ANOMALY_EVENTS, trackAnomalyEvent } from '../../consts';
import css from './SaveReportButton.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.SAVE_REPORT';
const UNAUTHORIZED_BOOKMARK_EDITING = 'STATIC.BUTTONS.TOOLTIP.DISABLE_SAVE';

const TOAST_AUTO_CLOSE = 3000;

const reportsApi = new Reports();
const topbarService = new TopbarService();

function SaveReportButton({
    queryParams = null,
    activeBookmark = null,
    onActiveBookmarkChange = () => {},
    translate = () => {},
}) {
    const [showPopover, setShowPopover] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const containerRef = useRef(null);

    const userName = useSelector(state => getUserData(state).username);
    const { getParamsObject, setParamsObject, removeParam } = useUrlParams();
    const [{ data: saveResponse, error: saveError }, runSave] = useAsync();

    // Both effects run once on mount but reference the current activeBookmark plus functions
    // that are fresh closures every render (getParamsObject/removeParam), so read them from a
    // ref to keep the effects dependency-free instead of re-running on every render.
    const latestRef = useRef({});
    latestRef.current = { getParamsObject, activeBookmark, onActiveBookmarkChange, removeParam };

    useEffect(() => {
        const latest = latestRef.current;
        const { bookmark_id: bookmarkId } = latest.getParamsObject();
        if (!bookmarkId) return;
        topbarService
            .getBookmarks()
            .then(({ bookmarks }) => {
                const bookmark = (bookmarks || []).find(({ uuid }) => uuid === bookmarkId);
                if (bookmark) latest.onActiveBookmarkChange(bookmark);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleBookmarkEvent = ({ detail }) => {
            const { type, data } = detail || {};
            const latest = latestRef.current;
            const activeUuid = latest.activeBookmark?.uuid;
            if (!activeUuid) return;
            if (type === 'bookmarkRemoved' && data === activeUuid) {
                latest.onActiveBookmarkChange(null);
                latest.removeParam('bookmark_id');
            } else if (type === 'bookmarkUpdated' && data?.uuid === activeUuid) {
                latest.onActiveBookmarkChange(data);
            }
        };
        window.addEventListener(BOOKMARK_EVENT, handleBookmarkEvent);
        return () => window.removeEventListener(BOOKMARK_EVENT, handleBookmarkEvent);
    }, []);

    useEffect(() => {
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setShowPopover(false);
            }
        };

        if (showPopover) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPopover]);

    useEffect(() => {
        const savedBookmark = saveResponse?.bookmark;
        if (!savedBookmark) return;

        setShowPopover(false);
        const bookmarkEventType = saveResponse.created ? 'bookmarkAdded' : 'bookmarkUpdated';

        onActiveBookmarkChange(savedBookmark);
        setParamsObject({ bookmark_id: savedBookmark.uuid, bookmark_creator: savedBookmark.creator });
        dispatchBookmarkEvent(bookmarkEventType, savedBookmark);
        toaster.success(translate(`${TRANSLATION_PREFIX}.SAVED_TOAST`), { autoClose: TOAST_AUTO_CLOSE });
    }, [saveResponse]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleBookmarkSave = bookmark => {
        if (!bookmark.name) {
            setValidationError(translate(`${TRANSLATION_PREFIX}.NAME_REQUIRED_ERROR`));
            return;
        }

        setValidationError(null);
        const { firstPeriod: ignoredFirstPeriod, ...query } = queryParams;
        trackAnomalyEvent(ANOMALY_EVENTS.SAVE_REPORT, {
            timePeriodType: bookmark.time_period_type,
            isPublic: bookmark.is_shared,
            isUpdate: !!activeBookmark,
        });
        runSave(
            reportsApi.saveBookmark({
                ...bookmark,
                query,
                id: activeBookmark?.uuid ?? getParamsObject().bookmark_id,
            })
        );
    };

    const isForeignBookmark = !!activeBookmark?.creator && activeBookmark.creator !== userName;
    const disabled = !queryParams || isForeignBookmark;
    const tooltipKey = !queryParams
        ? `${TRANSLATION_PREFIX}.DISABLED_TOOLTIP`
        : isForeignBookmark
        ? UNAUTHORIZED_BOOKMARK_EDITING
        : undefined;

    const requestError =
        saveError && (typeof saveError === 'string' ? saveError : translate(`${TRANSLATION_PREFIX}.SAVE_ERROR`));

    const popoverBookmarkData = useMemo(
        () => (activeBookmark?.name ? { ...activeBookmark, name: decodeBookmarkName(activeBookmark.name) } : null),
        [activeBookmark]
    );

    return (
        <div className={css.container} ref={containerRef}>
            <Button
                type="secondary"
                Icon="device-floppy"
                text={`${TRANSLATION_PREFIX}.BUTTON`}
                disabled={disabled}
                tooltipKey={tooltipKey}
                onClick={() => setShowPopover(current => !current)}
            />
            <BookmarkPopover
                type={ANOMALY_ANALYSIS_REPORT_TYPE}
                show={showPopover && !disabled}
                onSaveBookmark={handleBookmarkSave}
                onInnerShowChanged={setShowPopover}
                error={validationError || requestError || ''}
                bookmarkData={popoverBookmarkData}
                startDate={queryParams?.firstPeriod?.start || ''}
                endDate={queryParams?.firstPeriod?.end || ''}
            />
        </div>
    );
}

SaveReportButton.propTypes = {
    queryParams: PropTypes.shape({
        dimensions: PropTypes.arrayOf(PropTypes.string),
        colorMetric: PropTypes.string,
        sizeMetric: PropTypes.string,
        filters: PropTypes.arrayOf(PropTypes.shape({})),
        firstPeriod: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    }),
    activeBookmark: PropTypes.shape({
        uuid: PropTypes.string,
        name: PropTypes.string,
        creator: PropTypes.string,
    }),
    onActiveBookmarkChange: PropTypes.func,
    translate: PropTypes.func,
};

export default withLocalize(SaveReportButton);
